import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'react-native-toast-notifications';

import { StatusToast } from '@components/CustomToast/StatusToast.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import {
  GetTeamDetailDocument,
  ListChatsPaginationDocument,
  ListPendingUserRequestsDocument,
  useCreateUserRequestMutation,
  UserRequestable,
  UserRequestCategory,
} from '@graphql/generated';
import {
  hasPendingTeamJoinRequest,
  recordTeamJoinRequestOutcome,
} from '@utils/pendingTeamJoinRequests';

export const NonMemberContent = ({
  teamUUId,
  teamId,
  hasPendingRequest = false,
}: {
  teamUUId: string;
  teamId?: string;
  /**
   * Whether this user already has a pending join request, resolved once by
   * usePublicTeamFromQuery. A plain boolean rather than the PublicTeam
   * object: the previous shape lost the flag whenever getPublicTeam answered
   * with its Team branch, since only the PublicTeam branch was narrowed.
   */
  hasPendingRequest?: boolean;
}) => {
  const { t } = useTranslation('models');
  const toast = useToast();
  // Set as soon as the mutation resolves so the button flips to "Pending"
  // without waiting for getPublicTeam to come back.
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState(false);

  const requestableId = teamId || teamUUId;

  const [createUserRequest, { loading: isSubmittingRequest }] =
    useCreateUserRequestMutation({
      onCompleted: () => {
        recordTeamJoinRequestOutcome({ teamUUId });
        setHasSubmittedRequest(true);
      },
      // A duplicate is not a failure: the team link signup flow already
      // created the request, so the user is asking for what they have.
      onError: (error) => {
        if (recordTeamJoinRequestOutcome({ teamUUId, error })) {
          setHasSubmittedRequest(true);
          return;
        }
        // Anything else really did fail. The button stays enabled so the
        // user can retry, but silence here is the very "no feedback"
        // symptom TA-10772 was filed for.
        toast.show(
          <StatusToast
            message={t('models:teams.requestToJoinFailed')}
            duration={3000}
            type='error'
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
          }
        );
      },
      refetchQueries: [
        { query: ListChatsPaginationDocument, variables: { first: 20 } },
        'listMessages',
        // Re-reads hasPendingMemberAdditionRequest. TeamDetailPage resolves
        // it once and shares it through context, so all three tabs render
        // from the same value — without this the Members/Invoice tabs mount
        // on the stale `false` and offer "Request to Join" again.
        //
        // Must be getTeamDetail with includePending: true, matching what the
        // readers actually watch (TA-10850). getPublicTeam and getTeamDetail
        // are separate root-field cache entries, and PublicTeam has no
        // normalized key policy, so refetching the other one writes an entry
        // nobody reads and leaves the shared value stale — the sessionStorage
        // fallback only hides that for the tab that made the request.
        {
          query: GetTeamDetailDocument,
          variables: { id: teamUUId, includePending: true },
        },
        // Refreshes the admin's pending list so the new request shows up
        // there without a reload.
        {
          query: ListPendingUserRequestsDocument,
          variables: {
            attributes: {
              requestType: UserRequestCategory.MemberAddition,
              userRequestableId: requestableId,
              userRequestableType: UserRequestable.Account,
            },
          },
        },
      ],
    });

  // TA-10772: derived, not stored — the team link signup flow creates the
  // request before this screen mounts, so hasPendingRequest can arrive after
  // the first render.
  //
  // The third term is the QA round 2 fix. `hasPendingRequest` is the server's
  // hasPendingMemberAdditionRequest, and in the team link flow it comes back
  // `false` even though the request was created during registration, so the
  // button offered "Request to Join" again. The remembered flag is written by
  // whoever created the request — this component, or the post-signup effect in
  // app-stack.web — and is read on every render, so it also survives the
  // remount when the user switches between the Details / Members / Invoice
  // tabs, which each mount their own NonMemberContent with a fresh useState.
  const hasJoinRequested =
    hasSubmittedRequest ||
    hasPendingRequest ||
    hasPendingTeamJoinRequest(teamUUId);

  const handleRequestToJoin = () => {
    createUserRequest({
      variables: {
        attributes: {
          requestType: UserRequestCategory.MemberAddition,
          userRequestableId: requestableId,
          userRequestableType: UserRequestable.Account,
        },
      },
    });
  };

  const buttonText = hasJoinRequested
    ? t('models:projects.pendingRequest')
    : t('models:projects.requestToJoin');
  const buttonContentColor = hasJoinRequested ? 'grey04' : 'white';
  const buttonBackgroundColor = hasJoinRequested ? 'grey02' : 'textPrimary';
  const buttonVariant = hasJoinRequested
    ? 'editSelectedDisabledDark'
    : 'editWhite';
  // requestableId is empty only if the route lost teamUUId and no team
  // resolved — sending userRequestableId: '' would be a guaranteed 400.
  // In-flight is deliberately NOT part of `disabled`: Button's `isLoading`
  // already locks the press, and `disabled` would additionally grey the
  // button out, which reads as "unavailable" rather than "processing".
  const buttonDisabled = hasJoinRequested || !requestableId;
  const buttonBorderWidth = hasJoinRequested ? 1 : 0;
  const buttonAccessibilityLabel = buttonText;

  return (
    <Box flex={1} zIndex={100} justifyContent='center' alignItems='center'>
      <Box alignItems='center' justifyContent='center'>
        <Icon
          variant='xxl'
          color='textPrimary'
          name='AlertTriangle'
          marginRight='s'
        />
        <Text
          variant='heading2'
          color='textSecondary'
          marginBottom='m'
          marginTop='s'>
          {t('models:teams.nonMember')}
        </Text>

        <Box width={180} height={48}>
          <Button
            // Button falls back to getScaledSize(56) unless isSmall is set,
            // which overflowed this 48-tall wrapper. Matches
            // TaskInviteRequestButton, the same control done right.
            isSmall
            disabled={buttonDisabled}
            // Button renders its children inside <Text>, so a spinner passed
            // as a child would nest a View in a Text (a span on RN Web).
            // isLoading swaps the whole label for Button's own centered
            // spinner and pins the measured width so it doesn't resize.
            isLoading={isSubmittingRequest}
            backgroundColor={buttonBackgroundColor}
            prefix={
              <Icon variant='l' name='UserCheck' color={buttonContentColor} />
            }
            onPress={handleRequestToJoin}
            borderRadius='xl'
            variant={buttonVariant}
            prefixMarginRight='s'
            borderWidth={buttonBorderWidth}
            accessibilityLabel={buttonAccessibilityLabel}>
            {buttonText}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
